import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus,
  Trash2,
  Camera,
  Sparkles,
  Save,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  ExternalLink,
  FileText,
} from "lucide-react";
import { addQuizz, type Quizz, type QuizzQuestion } from "../lib/quizz-service";
import {
  saveQuizzDraft,
  loadQuizzDraft,
  clearQuizzDraft,
  hasDraftWithContent,
  isDraftOld,
  draftToQuizz,
  type QuizzDraft,
} from "../lib/quizz-draft";
import { quizzVisionService } from "@core/services/QuizzVisionService";
import { useToast } from "@core/hooks/use-toast";
import { cn } from "@core/lib/utils";
import { ErrorFactory, logError } from "@core/lib/error-handling";
import { Button } from "@core/components/ui/button";
import { Settings, Mail } from "lucide-react";
import { Label } from "@core/components/ui/label";
import { Input } from "@core/components/ui/input";
import { guestEmailService } from "@core/lib/guestEmailService";
import { useAuth } from "@core/contexts/AuthContext";
import { SafeguardSection } from "@core/components/polls/SafeguardSection";
import { fileToGeminiAttachment } from "@core/services/FileAttachmentService";
import PollSettingsForm from "@core/components/polls/PollSettingsForm";
import type { QuizzSettings } from "../types/quizz";

// Limites et formats supportés pour les fichiers utilisés en génération de quizz
const MAX_QUIZZ_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 Mo
// Préfixes MIME autorisés : images (PNG/JPEG, etc.) et PDF pour les devoirs scannés
const ALLOWED_QUIZZ_ATTACHMENT_MIME_PREFIXES = ["image/", "application/pdf"];
// Liste de types exacts éventuellement supportés à l'avenir (DOCX, etc.)
const ALLOWED_QUIZZ_ATTACHMENT_MIME_EXACT: string[] = [];

// Génère un slug simple à partir du titre
const generateSlug = (title: string): string => {
  return (
    title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Enlève les accents
      .replace(/[^a-z0-9]+/g, "-") // Remplace les caractères spéciaux par des tirets
      .replace(/^-|-$/g, "") // Enlève les tirets en début/fin
      .substring(0, 50) +
    "-" +
    Date.now().toString(36)
  ); // Ajoute un ID unique
};

interface QuizzCreateProps {
  onBack?: () => void;
}

export default function QuizzCreate({ onBack }: QuizzCreateProps = {}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // State for file upload flow
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState<QuizzQuestion[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [advancedSettings, setAdvancedSettings] = useState<QuizzSettings>({
    showLogo: true,
    showEstimatedTime: true,
    showQuestionCount: true,
    requireAuth: false,
    oneResponsePerPerson: true,
    maxResponses: undefined,
    resultsVisibility: "creator-only",
    allowRetry: false,
    showCorrectAnswers: true,
  });
  const { user: authUser } = useAuth();
  const currentUser = authUser;
  const [published, setPublished] = useState(false);
  const [publishedQuiz, setPublishedQuiz] = useState<Quizz | null>(null);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);

  // Auto-save draft state
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftCreatedAt, setDraftCreatedAt] = useState<string | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  // Charger l'email existant si guest + vérifier le draft
  React.useEffect(() => {
    // Vérifier s'il y a un draft à charger
    const draft = loadQuizzDraft();
    if (draft && hasDraftWithContent()) {
      setShowDraftBanner(true);
      if (isDraftOld()) {
        toast({
          title: "Ancien brouillon trouvé",
          description:
            "Un brouillon de plus de 24h est disponible. Vous pouvez le continuer ou commencer un nouveau quiz.",
          duration: 5000,
        });
      }
    }
  }, [currentUser, toast]);

  // Auto-save après chaque modification
  React.useEffect(() => {
    if (title || questions.length > 0) {
      const draft: QuizzDraft = {
        id:
          draftId ||
          `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title,
        description,
        questions,
        settings: advancedSettings,
        createdAt: draftCreatedAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      saveQuizzDraft(draft);

      // Mettre à jour l'ID du draft si nouveau
      if (!draftId) {
        setDraftId(draft.id);
        setDraftCreatedAt(draft.createdAt);
      }
    }
  }, [
    title,
    description,
    questions,
    advancedSettings,
    draftId,
    draftCreatedAt,
  ]);

  // Continuer le brouillon
  const handleContinueDraft = () => {
    const draft = loadQuizzDraft();
    if (draft) {
      setTitle(draft.title);
      setDescription(draft.description);
      setQuestions(draft.questions);
      setAdvancedSettings(draft.settings);
      setDraftId(draft.id);
      setDraftCreatedAt(draft.createdAt);
      setShowDraftBanner(false);

      toast({
        title: "Brouillon chargé",
        description: "Votre travail a été restauré.",
      });
    }
  };

  // Ignorer le brouillon
  const handleDismissDraft = () => {
    setShowDraftBanner(false);
    toast({
      title: "Nouveau quiz",
      description: "Vous pouvez commencer un nouveau quiz.",
    });
  };

  // Toggle question expansion
  const toggleQuestion = (questionId: string) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  };

  // Expand all questions
  const expandAll = () => {
    setExpandedQuestions(new Set(questions.map((q) => q.id)));
  };

  // Collapse all questions
  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  // Suppression de textPrompt et handleGenerateFromText car l'utilisateur ne souhaite pas de champ libre

  // Sélection de fichier (sans génération immédiate)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation basique
    if (file.size > MAX_QUIZZ_ATTACHMENT_SIZE_BYTES) {
      toast({
        title: "Fichier trop volumineux",
        description: "Le fichier dépasse 10 Mo.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
    // Reset inputs pour permettre de re-sélectionner le même fichier si besoin
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  // Traitement du fichier sélectionné
  const handleAnalyzeFile = async () => {
    if (!selectedFile) return;

    setIsGenerating(true);
    toast({
      title: "Analyse en cours...",
      description: "Gemini analyse votre document",
    });

    try {
      console.log("[QuizzCreate] Analyse fichier:", selectedFile.name);

      const attachment = await fileToGeminiAttachment(selectedFile);
      const result = await quizzVisionService.extractFromImage(
        attachment.contentBase64,
        attachment.mimeType,
      );

      if (result.success && result.data) {
        if (!title.trim()) {
          setTitle(result.data.title);
        }

        setQuestions(result.data.questions);
        toast({
          title: `✅ ${result.data.questions.length} questions extraites !`,
        });
      } else {
        if (result.error) {
          throw ErrorFactory.api(result.error);
        } else {
          throw ErrorFactory.api("Erreur lors de l'analyse");
        }
      }
    } catch (error: any) {
      logError(error, {
        component: "QuizzCreate",
        operation: "handleAnalyzeFile",
      });
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'analyser le fichier",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Reset pour recommencer
  const handleReset = () => {
    setSelectedFile(null);
    setTitle("");
    setQuestions([]);
  };

  // Ajouter une question manuellement
  const addQuestion = () => {
    const newQ: QuizzQuestion = {
      id: `q${Date.now()}`,
      question: "",
      type: "single",
      options: ["", ""],
      correctAnswer: "",
      points: 1,
    };
    setQuestions([...questions, newQ]);
    // Déplier automatiquement la nouvelle question
    setExpandedQuestions((prev) => new Set([...prev, newQ.id]));
  };

  // Mettre à jour une question
  const updateQuestion = (index: number, updates: Partial<QuizzQuestion>) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    setQuestions(updated);
  };

  // Supprimer une question
  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  // Valider qu'une question est complète
  const isQuestionComplete = (question: QuizzQuestion): boolean => {
    if (!question.question?.trim()) return false;

    if (question.type === "single" || question.type === "multiple") {
      if (!question.options || question.options.length < 2) return false;
      const optionsFilled = question.options.every((opt) => opt.trim() !== "");

      if (question.type === "single") {
        return (
          optionsFilled &&
          typeof question.correctAnswer === "string" &&
          question.correctAnswer !== ""
        );
      }

      if (question.type === "multiple") {
        return (
          optionsFilled &&
          Array.isArray(question.correctAnswer) &&
          question.correctAnswer.length > 0
        );
      }

      return optionsFilled;
    }

    if (question.type === "true-false") {
      return typeof question.correctAnswer === "boolean";
    }

    if (question.type === "text" || question.type === "text-ai") {
      return (
        typeof question.correctAnswer === "string" &&
        question.correctAnswer.trim().length > 0
      );
    }

    return true;
  };

  // Valider que toutes les questions sont complètes
  const areAllQuestionsValid = questions.every(isQuestionComplete);

  // Sauvegarder le quiz
  const handleSave = async () => {
    if (questions.length === 0) {
      toast({ title: "Ajoutez au moins une question", variant: "destructive" });
      return;
    }
    if (!areAllQuestionsValid) {
      toast({
        title: "Veuillez compléter toutes les questions",
        variant: "destructive",
      });
      return;
    }

    // Générer automatiquement un titre si non saisi
    let finalTitle = title.trim();
    if (!finalTitle) {
      // Générer un titre basé sur les questions ou le contenu
      const firstQuestion = questions[0]?.question || "";
      const subject = questions[0]?.explanation?.includes("math")
        ? "Mathématiques"
        : questions[0]?.explanation?.includes("français")
          ? "Français"
          : questions[0]?.explanation?.includes("histoire")
            ? "Histoire"
            : "Quiz";
      finalTitle = `Quiz ${subject} - ${new Date().toLocaleDateString("fr-FR")}`;

      // Mettre à jour le titre généré
      setTitle(finalTitle);
      toast({
        title: "Titre généré automatiquement",
        description: `Titre : "${finalTitle}"`,
        duration: 3000,
      });
    }

    const maxPoints = questions.reduce((sum, q) => sum + (q.points || 1), 0);
    const quiz: Quizz = {
      id: `quiz_${Date.now()}`,
      slug: generateSlug(finalTitle),
      title: finalTitle,
      description: "", // Description retirée
      type: "quizz",
      questions,
      status: "active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      creator_id: currentUser?.id || "local",
      maxPoints,
      settings: advancedSettings,
    };

    try {
      await addQuizz(quiz);
      setPublishedQuiz(quiz);
      setPublished(true);

      // Nettoyer le draft après création réussie
      clearQuizzDraft();

      toast({ title: "Quiz créé avec succès !" });

      // Note: L'écran de succès s'affiche via le return ci-dessous
    } catch (error) {
      toast({ title: "Erreur lors de la création", variant: "destructive" });
    }
  };

  // Écran de succès après publication
  if (published && publishedQuiz) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <div className="pt-20">
          <div className="max-w-2xl mx-auto p-4 sm:p-6">
            <div
              className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 rounded-xl border border-amber-700/30 p-8 text-center space-y-6"
              data-testid="quiz-success-screen"
            >
              {/* Icône de succès */}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Check className="w-10 h-10 text-amber-400" />
                </div>
              </div>

              {/* Message de succès */}
              <div>
                <h1
                  className="text-3xl font-bold text-white mb-2"
                  data-testid="quiz-success-title"
                >
                  Quiz publié !
                </h1>
                <p className="text-gray-300">
                  Votre quiz "{publishedQuiz.title}" est maintenant actif et
                  prêt à recevoir des réponses.
                </p>
              </div>

              {/* Message d'information pour la bêta */}
              {/* <div className="p-4 bg-blue-500/10 border border-blue-600/30 rounded-lg">
                <div className="flex items-center gap-2 text-blue-400">
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium">Information bêta</span>
                </div>
                <p className="text-sm text-blue-300 mt-1">
                  Pour finaliser et partager votre quiz, après la bêta, vous devrez vous connecter
                  ou créer un compte.
                </p>
              </div> */}

              {/* Actions */}
              {!currentUser && (
                <div className="mb-6 max-w-md mx-auto">
                  <SafeguardSection />
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Link
                  to="/quizz/dashboard"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg font-semibold transition-all shadow-lg"
                  data-testid="quiz-go-to-dashboard"
                >
                  <Check className="w-5 h-5" />
                  Aller au Tableau de bord
                </Link>
                <Link
                  to={`/quizz/${publishedQuiz.slug || publishedQuiz.id}/vote`}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-amber-300 border-2 border-amber-600/50 rounded-lg font-semibold hover:bg-amber-900/30 transition-colors"
                  data-testid="quiz-view-quiz"
                >
                  <ExternalLink className="w-5 h-5" />
                  Voir le quiz
                </Link>
              </div>

              {/* Lien de partage */}
              <div className="pt-4 border-t border-amber-700/50">
                <p className="text-sm text-gray-400 mb-3">Lien de partage :</p>
                <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                  <code
                    className="px-4 py-2 bg-[#1e1e1e] border border-amber-700/50 rounded text-sm font-mono text-gray-300 break-all"
                    data-testid="quiz-share-link"
                  >
                    {window.location.origin}/quizz/
                    {publishedQuiz.slug || publishedQuiz.id}/vote
                  </code>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/quizz/${publishedQuiz.slug || publishedQuiz.id}/vote`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: "Lien copié !",
                        description:
                          "Le lien a été copié dans le presse-papiers.",
                      });
                    }}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
                    data-testid="quiz-copy-link"
                  >
                    Copier
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6 pt-2 pb-4">
      {/* Banner de brouillon */}
      {showDraftBanner && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
            <div>
              <p className="text-amber-300 font-medium">Brouillon disponible</p>
              <p className="text-amber-400 text-sm">
                Continuer votre travail précédent ?
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleContinueDraft}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
              data-testid="quizzcreate-continuer"
            >
              Continuer
            </button>
            <button
              onClick={handleDismissDraft}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors"
              data-testid="quizzcreate-ignorer"
            >
              Ignorer
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center sm:text-left">
        <h1 className="text-xl sm:text-2xl font-bold text-white">
          Créer un Quiz
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Générez un quiz depuis un fichier (photo, PDF...) ou du texte
        </p>
      </div>

      {/* Génération IA */}
      <div className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 rounded-xl p-4 sm:p-6 border border-amber-700/30">
        <h2 className="font-semibold text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2 text-amber-300">
          <Sparkles className="h-5 w-5 text-amber-400 flex-shrink-0" />
          Génération IA
        </h2>

        {/* Option 1: Fichier (photo, PDF, ...) */}
        <div className="mb-3 sm:mb-4">
          {/* Input pour sélectionner depuis la galerie */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          {/* Input pour prendre une photo (caméra) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => cameraInputRef.current?.click()}
              disabled={isGenerating || !!selectedFile}
              variant="outline"
              className="flex items-center justify-center gap-1.5 sm:gap-2 border-amber-600/50 hover:border-amber-500 hover:bg-gray-800 bg-gray-800/50"
              data-testid="quizzcreate-button"
            >
              <Camera className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 flex-shrink-0" />
              <span className="font-medium text-amber-300 text-xs sm:text-sm truncate">
                Prendre une photo
              </span>
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isGenerating || !!selectedFile}
              variant="outline"
              className="flex items-center justify-center gap-1.5 sm:gap-2 border-gray-600 hover:border-gray-500 hover:bg-gray-800 bg-gray-800/50"
              data-testid="quizzcreate-button"
            >
              <span className="font-medium text-gray-300 text-xs sm:text-sm truncate">
                📎 Fichier (photo/PDF)
              </span>
            </Button>
          </div>

          {/* Prévisualisation du fichier sélectionné */}
          {selectedFile && (
            <div className="mt-3 bg-gray-900/50 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-10 h-10 rounded bg-gray-800 flex items-center justify-center flex-shrink-0">
                    {selectedFile.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="Aperçu"
                        className="w-full h-full object-cover rounded"
                      />
                    ) : (
                      <FileText className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate max-w-[150px] sm:max-w-[200px]">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-400">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-1.5 hover:bg-gray-800 rounded-full text-gray-400 hover:text-red-400 transition-colors"
                  data-testid="quizzcreate-button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Bouton d'analyse après upload */}
          {selectedFile && (
            <div className="mt-3 bg-gray-900/50 border border-amber-500/30 rounded-lg p-4">
              <Button
                onClick={handleAnalyzeFile}
                disabled={isGenerating}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white shadow-lg shadow-amber-900/20"
                data-testid="quizzcreate-button"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyse en
                    cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Créer le quiz
                  </>
                )}
              </Button>

              <div className="mt-3 pt-3 border-t border-gray-700">
                <button
                  onClick={handleReset}
                  className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
                >
                  ← Changer de fichier
                </button>
              </div>
            </div>
          )}

          {!selectedFile && (
            <p className="text-xs text-gray-400 mt-2 text-center">
              Uploadez un fichier (photo, PDF) pour générer un quiz
            </p>
          )}
        </div>
      </div>

      {/* Infos de base - toujours visible */}
      <div className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-gray-700 space-y-3 sm:space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Titre du quiz *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex: Quiz Mathématiques - CE2"
            className="w-full px-3 sm:px-4 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            data-testid="quiz-title-input"
          />
          {!title.trim() && (
            <p className="text-xs text-amber-400 mt-1">
              💡 Laissez vide pour que l'IA génère automatiquement un titre
            </p>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-semibold text-base sm:text-lg text-white">
            Questions ({questions.length})
          </h2>
          <div className="flex items-center gap-2">
            {questions.length > 0 && (
              <button
                onClick={
                  expandedQuestions.size === questions.length
                    ? collapseAll
                    : expandAll
                }
                className="text-xs text-gray-400 hover:text-white transition-colors"
                data-testid="quizzcreate-button"
              >
                {expandedQuestions.size === questions.length
                  ? "Tout replier"
                  : "Tout déplier"}
              </button>
            )}
            <Button
              onClick={addQuestion}
              size="sm"
              variant="outline"
              className="inline-flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 hover:text-white transition-colors border-gray-700"
              data-testid="add-question-button"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Ajouter</span>
            </Button>
          </div>
        </div>

        {questions.map((q, idx) => {
          const isExpanded = expandedQuestions.has(q.id);
          return (
            <div
              key={q.id}
              className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden"
            >
              {/* Question Header - Always visible */}
              <div
                className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 cursor-pointer hover:bg-gray-800/30 transition-colors"
                onClick={() => toggleQuestion(q.id)}
              >
                <button
                  className="text-gray-400 flex-shrink-0"
                  data-testid="quizzcreate-button"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-gray-500 block">
                    Question {idx + 1}
                  </span>
                  <p className="text-sm sm:text-base text-white truncate">
                    {q.question || (
                      <span className="text-gray-500 italic">Sans titre</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {q.options && (
                    <span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-0.5 rounded hidden sm:inline">
                      {q.options.length} options
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeQuestion(idx);
                    }}
                    className="p-1.5 sm:p-2 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                    data-testid="quizzcreate-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Question Details - Collapsible */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-200",
                  isExpanded
                    ? "max-h-[1000px] opacity-100"
                    : "max-h-0 opacity-0",
                )}
              >
                <div className="p-3 sm:p-4 pt-0 space-y-4 border-t border-gray-700/50">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Type de question */}
                    <div className="sm:col-span-1">
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                        Type
                      </label>
                      <select
                        value={q.type}
                        onChange={(e) => {
                          const newType = e.target.value as any;
                          // Reset des réponses selon le type
                          let newCorrectAnswer = q.correctAnswer;
                          let newOptions = q.options;

                          if (newType === "true-false") {
                            newCorrectAnswer = true;
                            newOptions = undefined;
                          } else if (
                            newType === "text" ||
                            newType === "text-ai"
                          ) {
                            newCorrectAnswer = "";
                            newOptions = undefined;
                          } else if (
                            newType === "single" ||
                            newType === "multiple"
                          ) {
                            if (!newOptions || newOptions.length === 0)
                              newOptions = ["", ""];
                            if (
                              newType === "single" &&
                              typeof newCorrectAnswer !== "string"
                            )
                              newCorrectAnswer = "";
                            if (
                              newType === "multiple" &&
                              !Array.isArray(newCorrectAnswer)
                            )
                              newCorrectAnswer = [];
                          }

                          updateQuestion(idx, {
                            type: newType,
                            correctAnswer: newCorrectAnswer,
                            options: newOptions,
                          });
                        }}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-amber-500 font-medium"
                      >
                        <option value="single">Choix unique (QCM)</option>
                        <option value="multiple">Choix multiple</option>
                        <option value="true-false">Vrai / Faux</option>
                        <option value="text">Réponse courte</option>
                        <option value="text-ai">Réponse IA</option>
                      </select>
                    </div>

                    {/* Question text */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                        Texte de la question
                      </label>
                      <input
                        type="text"
                        value={q.question}
                        onChange={(e) =>
                          updateQuestion(idx, { question: e.target.value })
                        }
                        placeholder="Entrez la question..."
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  {/* Options / Réponses selon le type */}
                  {(q.type === "single" || q.type === "multiple") &&
                    q.options && (
                      <div className="space-y-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-300">
                          Options (bonne réponse en premier)
                        </label>
                        {q.options.map((opt, optIdx) => {
                          const isCorrect =
                            q.type === "single"
                              ? q.correctAnswer === opt
                              : Array.isArray(q.correctAnswer) &&
                                q.correctAnswer.includes(opt);

                          return (
                            <div
                              key={optIdx}
                              className="flex items-center gap-2"
                            >
                              {/* Checkbox / Radio de sélection de bonne réponse */}
                              <button
                                type="button"
                                onClick={() => {
                                  if (q.type === "single") {
                                    updateQuestion(idx, {
                                      correctAnswer: opt,
                                    });
                                  } else {
                                    // Multiple
                                    const currentCorrect = Array.isArray(
                                      q.correctAnswer,
                                    )
                                      ? [...q.correctAnswer]
                                      : [];
                                    const newCorrect = currentCorrect.includes(
                                      opt,
                                    )
                                      ? currentCorrect.filter((a) => a !== opt)
                                      : [...currentCorrect, opt];
                                    updateQuestion(idx, {
                                      correctAnswer: newCorrect,
                                    });
                                  }
                                }}
                                className={cn(
                                  "w-6 h-6 rounded flex items-center justify-center border transition-all",
                                  isCorrect
                                    ? "bg-green-600 border-green-600 text-white"
                                    : "border-gray-600 hover:border-gray-500",
                                  q.type === "single" && "rounded-full",
                                )}
                                title="Marquer comme bonne réponse"
                                data-testid="quizzcreate-button"
                              >
                                {isCorrect && <Check className="w-3 h-3" />}
                              </button>

                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => {
                                  const newVal = e.target.value;
                                  const newOpts = [...(q.options || [])];
                                  newOpts[optIdx] = newVal;

                                  // Mettre à jour correctAnswer si l'option modifiée était une bonne réponse
                                  let newCorrectAnswer = q.correctAnswer;
                                  if (q.type === "single") {
                                    if (q.correctAnswer === opt)
                                      newCorrectAnswer = newVal;
                                  } else {
                                    if (
                                      Array.isArray(q.correctAnswer) &&
                                      q.correctAnswer.includes(opt)
                                    ) {
                                      newCorrectAnswer = (
                                        q.correctAnswer as string[]
                                      ).map((a) => (a === opt ? newVal : a));
                                    }
                                  }

                                  updateQuestion(idx, {
                                    options: newOpts,
                                    correctAnswer: newCorrectAnswer,
                                  });
                                }}
                                placeholder={`Option ${optIdx + 1}`}
                                className={cn(
                                  "flex-1 px-3 py-2 border rounded-lg text-white text-sm placeholder:text-gray-500",
                                  isCorrect
                                    ? "border-green-600/50 bg-green-900/20"
                                    : "border-gray-700 bg-gray-900",
                                )}
                              />
                            </div>
                          );
                        })}
                        <Button
                          onClick={() => {
                            const newOpts = [...(q.options || []), ""];
                            updateQuestion(idx, { options: newOpts });
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-auto px-0 text-xs sm:text-sm text-amber-400 hover:text-amber-300 hover:bg-transparent"
                          data-testid="quizzcreate--ajouter-une-option"
                        >
                          + Ajouter une option
                        </Button>
                      </div>
                    )}

                  {q.type === "true-false" && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-2">
                        Bonne réponse
                      </label>
                      <div className="flex gap-4">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuestion(idx, { correctAnswer: true })
                          }
                          className={cn(
                            "flex-1 py-2 px-4 rounded-lg font-medium border-2 transition-all",
                            q.correctAnswer === true
                              ? "border-green-500 bg-green-500/20 text-green-400"
                              : "border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600",
                          )}
                          data-testid="quizzcreate-vrai"
                        >
                          Vrai
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            updateQuestion(idx, { correctAnswer: false })
                          }
                          className={cn(
                            "flex-1 py-2 px-4 rounded-lg font-medium border-2 transition-all",
                            q.correctAnswer === false
                              ? "border-red-500 bg-red-500/20 text-red-400"
                              : "border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600",
                          )}
                          data-testid="quizzcreate-faux"
                        >
                          Faux
                        </button>
                      </div>
                    </div>
                  )}

                  {(q.type === "text" || q.type === "text-ai") && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                        Réponse attendue
                      </label>
                      <input
                        type="text"
                        value={(q.correctAnswer as string) || ""}
                        onChange={(e) =>
                          updateQuestion(idx, {
                            correctAnswer: e.target.value,
                          })
                        }
                        placeholder="La réponse correcte..."
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:ring-2 focus:ring-green-500/50 border-green-600/30"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {q.type === "text-ai"
                          ? "L'IA validera les réponses proches (synonymes, fautes légères...)"
                          : "La réponse devra correspondre exactement (insensible à la casse)."}
                      </p>
                    </div>
                  )}

                  {/* Explication */}
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">
                      Explication (optionnel)
                    </label>
                    <input
                      type="text"
                      value={q.explanation || ""}
                      onChange={(e) =>
                        updateQuestion(idx, { explanation: e.target.value })
                      }
                      placeholder="Pourquoi cette réponse est correcte..."
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Paramètres avancés */}
      {questions.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Header cliquable pour ouvrir/fermer */}
          <button
            onClick={() => setAdvancedSettingsOpen(!advancedSettingsOpen)}
            className="w-full flex items-center justify-between gap-2 p-4 sm:p-6 hover:bg-gray-700/50 transition-colors"
            data-testid="quizzcreate-button"
          >
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              <h3 className="text-lg font-semibold text-white">
                Paramètres avancés
              </h3>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${
                advancedSettingsOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Contenu des paramètres - affiché seulement si ouvert */}
          {advancedSettingsOpen && (
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 border-t border-gray-700">
              <PollSettingsForm
                settings={advancedSettings}
                onSettingsChange={(newSettings) =>
                  setAdvancedSettings(newSettings as QuizzSettings)
                }
                pollType="date"
              />
            </div>
          )}
        </div>
      )}

      {/* Actions - Fixed on mobile */}
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 pb-8">
        <button
          onClick={() => navigate("/quizz")}
          className="w-full sm:w-auto px-6 py-2.5 text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 hover:text-white transition-colors text-sm font-medium"
          data-testid="quizzcreate-annuler"
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          disabled={
            !title.trim() || questions.length === 0 || !areAllQuestionsValid
          }
          className="w-full sm:w-auto px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium"
          data-testid="finalize-quizz"
        >
          <Save className="h-4 w-4 sm:h-5 sm:w-5" />
          Créer le quiz
        </button>
      </div>
    </div>
  );
}
